import { z } from 'zod';
import { alertActorSchema, alertSeveritySchema, cargoStateSchema, closedReasonSchema, conveyanceKindSchema, dataSourceSchema, dataStatusSchema, legStatusSchema, shipmentStatusSchema, transportModeSchema, } from '../enums.js';
import { EVENT_WIRE_CODES } from './event-catalogue.js';
// The payload Plancia pushes to Dokless: `plancia.tracking/v2`
// (06-dokless-integration.md §3, 06-dokless-openapi.yaml). Every key of a
// `full` payload is always present; unknown values are `null` or `[]`, never a
// guess. Objects are not strict: minor versions may add keys, and Dokless must
// store what it does not understand yet.
export const TRACKING_SCHEMA_ID = 'plancia.tracking/v2';
/** ISO-8601 UTC timestamp with `Z`. */
export const isoDateTime = z.iso.datetime({ offset: false });
/** `YYYY-MM-DD` (carrier schedules give dates without time). */
export const isoDate = z.iso.date();
/** A planned date can be a day or a precise time. */
export const dateOrDateTime = z.union([isoDate, isoDateTime]);
/**
 * A carrier time: the hour written on the carrier's page, labelled with the
 * Europe/Rome offset of that day (`2026-09-16T16:41:00+02:00`), as Dokless
 * reads v1's zoneless times (ADR 0064). `Z` is still accepted.
 */
export const carrierDateTime = z.iso.datetime({ offset: true });
/** A carrier day (`YYYY-MM-DD`) or a carrier time. */
export const carrierDateOrDateTime = z.union([isoDate, carrierDateTime]);
const nullable = (schema) => schema.nullable();
export const unlocodeSchema = z.string().regex(/^[A-Z]{2}[A-Z2-9]{3}$/, 'UN/LOCODE');
export const localizedTextSchema = z.object({
    it: z.string().max(800),
    en: z.string().max(800),
});
export const locationSchema = z.object({
    name: nullable(z.string()),
    unlocode: nullable(unlocodeSchema).optional(),
    iata: nullable(z.string()).optional(),
    lat: nullable(z.number().min(-90).max(90)).optional(),
    lon: nullable(z.number().min(-180).max(180)).optional(),
});
export const conveyanceIdentitySchema = z.object({
    kind: conveyanceKindSchema,
    name: nullable(z.string()).optional(),
    imo: nullable(z.string()).optional(),
    mmsi: nullable(z.string()).optional(),
    registration: nullable(z.string()).optional(),
});
export const NAV_STATUSES = ['underway', 'anchored', 'moored', 'restricted', 'unknown'];
/** How exact a fix is: AIS decimals, a page's whole degrees (±50 km), or read off a map (ADR 0099). */
export const POSITION_PRECISIONS = ['decimals', 'degrees', 'map'];
export const conveyancePositionSchema = conveyanceIdentitySchema.extend({
    lat: z.number().min(-90).max(90),
    lon: z.number().min(-180).max(180),
    speed_knots: nullable(z.number()).optional(),
    course_deg: nullable(z.number()).optional(),
    heading_deg: nullable(z.number()).optional(),
    nav_status: nullable(z.enum(NAV_STATUSES)).optional(),
    destination_text: nullable(z.string()).optional(),
    ais_eta: nullable(isoDateTime).optional(),
    /** Fix time. */
    at: isoDateTime,
    /** True when older than 12 h. */
    stale: z.boolean(),
    /** `map`: read off MarineTraffic's map, approximate (ADR 0099); `radius_km` says how much. */
    precision: z.enum(POSITION_PRECISIONS),
    /** `precision: map`: how far the real position may be, in km. Absent otherwise. */
    radius_km: nullable(z.int().min(1).max(500)).optional(),
    source: z.string(),
    /** Legacy MarineTraffic text block. */
    ais_status: nullable(z.record(z.string(), z.unknown())).optional(),
});
/** Legacy alias of `conveyance` with the keys Dokless's frontend reads today. */
export const legacyVesselSchema = z.object({
    name: nullable(z.string()),
    imo: nullable(z.string()),
    mmsi: nullable(z.string()),
    latitude: z.number(),
    longitude: z.number(),
    zone: nullable(z.string()).optional(),
    destination: nullable(z.string()).optional(),
    speed_knots: nullable(z.number()).optional(),
    course_deg: nullable(z.number()).optional(),
    eta: nullable(z.string()).optional(),
    reported_at: isoDateTime,
    precision: z.enum(POSITION_PRECISIONS),
    source: z.string(),
    ais_status: nullable(z.record(z.string(), z.unknown())).optional(),
});
export const datePairSchema = z.object({
    planned: nullable(carrierDateOrDateTime),
    actual: nullable(carrierDateTime),
});
export const legSchema = z.object({
    seq: z.int().min(1),
    from: locationSchema,
    to: locationSchema,
    conveyance: nullable(conveyanceIdentitySchema),
    voyage: nullable(z.string()),
    departure: datePairSchema,
    arrival: datePairSchema,
    status: legStatusSchema,
    source: dataSourceSchema,
    /** `to` is a transshipment location. */
    transshipment: z.boolean(),
});
export const eventSchema = z.object({
    /** Stable Plancia id; Dokless uses it for withdrawal. */
    id: z.string().min(1),
    /** A normalised event code (`GET /dokless/v2/event-catalogue`: its Dokless type and labels). */
    code: z.enum(EVENT_WIRE_CODES),
    status: dataStatusSchema,
    /** Legacy: `status === 'actual'`. */
    actual: z.boolean(),
    date: carrierDateOrDateTime,
    date_only: z.boolean().optional(),
    location: nullable(z.string()),
    /** Additive (ADR 0104): the carrier's own code of the place («VECON»), written apart from its name. */
    location_code: nullable(z.string().max(16)).optional(),
    location_unlocode: nullable(unlocodeSchema).optional(),
    latitude: nullable(z.number()).optional(),
    longitude: nullable(z.number()).optional(),
    /** Legacy conveyance name. */
    vessel: nullable(z.string()),
    conveyance: nullable(conveyanceIdentitySchema).optional(),
    voyage: nullable(z.string()),
    container: nullable(z.string()).optional(),
    source: dataSourceSchema,
    observed_by: z.literal('carrier'),
    confirmed_by: z.array(z.enum(['ais', 'port_call', 'carrier'])).optional(),
    original_text: nullable(z.string()).optional(),
    /**
     * The port's role in the voyage (rules v1 §5.1, ADR 0079): Dokless's origin or destination, a real
     * transshipment (discharge and loading on another vessel, both happened), or a call. `null` without a place.
     */
    role: nullable(z.enum(['origin', 'call', 'transshipment', 'destination'])).optional(),
});
export const alertParamsSchema = z
    .record(z.string(), z.string().max(255))
    .refine((params) => Object.keys(params).length <= 20, 'at most 20 params');
export const alertSchema = z.object({
    code: z.string().regex(/^[a-z][a-z0-9_]{1,63}$/),
    severity: alertSeveritySchema,
    actor: alertActorSchema,
    since: isoDateTime,
    updated_at: isoDateTime,
    params: alertParamsSchema,
    title: localizedTextSchema,
    message: localizedTextSchema,
    action: localizedTextSchema,
});
const pointSchema = z.tuple([z.number(), z.number()]);
/**
 * A piece of the road sailed (ADR 0076). `estimated: true`: guessed between a
 * port the carrier named and the vessel's first real position (draw it dashed,
 * «percorso stimato»); `false`: drawn from real positions. A gap between two
 * pieces is a stretch Plancia does not know: nothing is drawn there.
 */
export const sailedSegmentSchema = z.object({
    estimated: z.boolean(),
    points: z.array(pointSchema).max(1000),
});
export const routeSchema = z.object({
    /** Legacy, unchanged: the road sailed as one line (after the last gap; guessed stretches included). */
    sailed: z.array(pointSchema).max(1000),
    /** Additive (ADR 0076): the road sailed piece by piece, the guessed pieces marked. */
    sailed_segments: z.array(sailedSegmentSchema).max(50).optional(),
    /** Additive (ADR 0076): some of the road sailed is guessed (`sailed_segments[].estimated`). */
    sailed_estimated: z.boolean().optional(),
    /** Additive (ADR 0076): the whole route in one line, «via Malacca, Cape Of Good Hope, Gibraltar»; null if nothing notable. */
    via: nullable(z.string()).optional(),
    ahead: z.array(pointSchema).max(1000),
    ports: z
        .array(z.object({
        name: z.string(),
        unlocode: nullable(z.string()).optional(),
        /** Additive (ADR 0104): the carrier's own code of the place, apart from its name. */
        location_code: nullable(z.string().max(16)).optional(),
        latitude: z.number(),
        longitude: z.number(),
        done: z.boolean(),
        destination: z.boolean(),
        /** Additive (ADR 0104): `ais` — a call the vessel declares by AIS, not in the carrier's plan. */
        source: z.literal('ais').optional(),
        /** Additive (ADR 0104): the AIS ETA there, for an AIS call. */
        eta: nullable(isoDateTime).optional(),
    }))
        .max(50),
    blocked: nullable(z.object({
        toward: z.string(),
        passages: z.array(z.string()).max(5),
    })),
});
export const UNRELIABLE_REASONS = [
    'vessel_stopped',
    'other_destination',
    'route_earlier_than_schedule',
];
export const etaEstimateSchema = z.object({
    port: z.string(),
    unlocode: nullable(z.string()).optional(),
    at: isoDateTime,
    basis: z.enum(['vessel_ais_eta', 'route']),
    distance_km: nullable(z.number()).optional(),
    speed_knots: nullable(z.number()).optional(),
    position_at: nullable(isoDateTime).optional(),
    carrier_eta: nullable(carrierDateOrDateTime).optional(),
    /** Positive = Plancia later than the carrier. */
    diff_hours: nullable(z.number()).optional(),
    tolerance_hours: z.int(),
    reliable: z.boolean(),
    unreliable_reasons: z.array(z.enum(UNRELIABLE_REASONS)).optional(),
    computed_at: isoDateTime,
});
const envelopeShape = {
    schema: z.literal(TRACKING_SCHEMA_ID),
    revision: z.int().min(0),
    shipment_id: z.string().min(1),
    dokless_id: z.string().min(1),
    generated_at: isoDateTime,
    transport_mode: transportModeSchema,
};
export const fullPayloadSchema = z.object({
    ...envelopeShape,
    kind: z.literal('full'),
    status: shipmentStatusSchema,
    attention: z.boolean(),
    cargo_state: cargoStateSchema,
    /**
     * Legacy: = `cargo_state`. Dokless's ingest of today reads this key (v1 sent it): with `vessel: null`,
     * `at_origin`/`at_transshipment` clear its current vessel and voyage (PlanciaManager.php:198, owner 17/10).
     */
    cargo: cargoStateSchema,
    /** Legacy: true only when status = closed with arrival/delivery. */
    arrived: z.boolean(),
    closed_reason: nullable(closedReasonSchema),
    origin: locationSchema,
    destination: locationSchema,
    departure: z.object({
        planned: nullable(carrierDateOrDateTime),
        actual: nullable(carrierDateTime),
        source: dataSourceSchema.nullable().optional(),
    }),
    arrival: z.object({
        planned: nullable(carrierDateOrDateTime),
        estimated: nullable(isoDateTime),
        actual: nullable(carrierDateTime),
        carrier_eta: nullable(carrierDateOrDateTime),
        plancia_eta: nullable(isoDateTime),
        reliable: nullable(z.boolean()),
    }),
    /** Legacy carrier ETA at destination (null + eta_withdrawn when unreliable). */
    eta: nullable(carrierDateTime),
    eta_withdrawn: z.boolean(),
    legs: z.array(legSchema),
    events: z.array(eventSchema).max(500),
    events_complete: z.boolean(),
    /** Null unless cargo_state = on_board. */
    conveyance: nullable(conveyancePositionSchema),
    vessel: nullable(legacyVesselSchema),
    route: nullable(routeSchema),
    eta_estimate: nullable(etaEstimateSchema),
    alerts: z.array(alertSchema).max(12),
    /** Legacy: first blocking alert. */
    problem: nullable(alertSchema),
    /** Legacy: non-blocking alerts. */
    exceptions: z.array(alertSchema),
    next_check_at: nullable(isoDateTime),
    /** Legacy key kept for the UI (= status). */
    phase: z.string(),
    next_check_reason: nullable(z.string()).optional(),
});
export const positionPayloadSchema = z.object({
    ...envelopeShape,
    kind: z.literal('position'),
    cargo_state: cargoStateSchema,
    conveyance: conveyancePositionSchema,
    vessel: legacyVesselSchema,
    eta_estimate: nullable(etaEstimateSchema),
});
export const trackingPayloadSchema = z.discriminatedUnion('kind', [fullPayloadSchema, positionPayloadSchema]);
//# sourceMappingURL=tracking-v2.schema.js.map